import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, inject } from '@angular/core';
import { DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Store } from '@ngrx/store';

// PrimeNG imports
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';

// Models and services
import { 
  StaffWithPlans, 
  StaffPlanAssignmentsResponse,
  CommissionPlanResponse,
  StaffStatus,
  PlanStatus,
  PlanStaffAssignmentStatus
} from '../../models';
import { CommissionPlanService } from '../../services';
import { ToastMessageService } from '@fitness-force/api';
import { BreadcrumbComponent } from '@fitness-force/common/breadcrumb';
import { TENANTS_INFO } from '@fitness-force/state';
import type { TENANTSINTERFACE } from '@fitness-force/models';
import { AppErrors } from '@fitness-force/models';

@Component({
  selector: 'lib-commission-plan-staff-assigned',
  standalone: true,
  imports: [
    FormsModule,
    TranslateModule,
    TableModule,
    ButtonModule,
    TagModule,
    DialogModule,
    SelectModule,
    ToastModule,
    BreadcrumbComponent,
    DatePipe
],
  providers: [MessageService],
  templateUrl: './commission-plan-staff-assigned.component.html',
  styleUrl: './commission-plan-staff-assigned.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CommissionPlanStaffAssignedComponent implements OnInit {
  private commissionPlanService = inject(CommissionPlanService);
  private toastMessageService = inject(ToastMessageService);
  private messageService = inject(MessageService);
  private translateService = inject(TranslateService);
  private cdr = inject(ChangeDetectorRef);
  private store = inject(Store);

  staffList: StaffWithPlans[] = [];
  expandedRows: { [key: string]: boolean } = {};
  loading = false;
  
  // Pagination
  totalRecords = 0;
  currentPage = 1;
  pageSize = 10;
  
  // Dialog for assigning plan
  showAssignDialog = false;
  selectedStaffForAssignment: StaffWithPlans | null = null;
  availablePlans: CommissionPlanResponse[] = [];
  selectedPlanId: number | null = null;
  loadingPlans = false;
  assigningPlan = false;
  
  // Tenant info
  tenantId = 0;
  
  // Enums for template
  StaffStatus = StaffStatus;
  PlanStatus = PlanStatus;
  PlanStaffAssignmentStatus = PlanStaffAssignmentStatus;

  ngOnInit(): void {
    this.store.select(TENANTS_INFO).subscribe((tenant: TENANTSINTERFACE) => {
      if (tenant?.selected_tenant?.id) {
        this.tenantId = tenant.selected_tenant?.id;
        this.loadStaffPlanAssignments();
      }
    });
  }

  loadStaffPlanAssignments(): void {
    this.loading = true;
    this.cdr.markForCheck();

    this.commissionPlanService.getStaffPlanAssignments(this.tenantId, this.currentPage, this.pageSize)
      .subscribe({
        next: (response: StaffPlanAssignmentsResponse) => {
          // Filter to show only plans with active plan status AND active assignment status
          const staffWithActivePlans = response.items.map(item => ({
            ...item,
            plans: item.plans.filter(plan => 
              plan.plan_status === PlanStatus.Active && 
              plan.plan_staff_assignment_status === PlanStaffAssignmentStatus.Active
            )
          }));
          this.staffList = staffWithActivePlans || [];
          this.totalRecords = response.total_items || 0;
          this.currentPage = response.current_page || 1;
          this.loading = false;
          this.cdr.markForCheck();
        },
        error: (error) => {
          console.error('Error loading staff plan assignments:', error);
          this.toastMessageService.showError(
            this.translateService.instant('COMMISSION_MANAGEMENT.STAFF_ASSIGNMENTS.ERROR_LOADING')
          );
          this.loading = false;
          this.cdr.markForCheck();
        }
      });
  }

  onPageChange(event: any): void {
    this.currentPage = event.page + 1;
    this.pageSize = event.rows;
    this.loadStaffPlanAssignments();
  }

  onRowExpand(event: any): void {
    console.log('Row expanded:', event.data);
    this.expandedRows[event.data.staff_id] = true;
    this.cdr.markForCheck();
  }

  onRowCollapse(event: any): void {
    console.log('Row collapsed:', event.data);
    delete this.expandedRows[event.data.staff_id];
    this.cdr.markForCheck();
  }

  expandAll(): void {
    this.expandedRows = {};
    this.staffList.forEach(staff => {
      this.expandedRows[staff.staff_id] = true;
    });
    this.cdr.markForCheck();
  }

  collapseAll(): void {
    this.expandedRows = {};
    this.cdr.markForCheck();
  }

  openAssignPlanDialog(staff: StaffWithPlans): void {
    this.selectedStaffForAssignment = staff;
    this.selectedPlanId = null;
    this.showAssignDialog = true;
    this.loadAvailablePlans();
    this.cdr.markForCheck();
  }

  loadAvailablePlans(): void {
    this.loadingPlans = true;
    this.cdr.markForCheck();

    this.commissionPlanService.getTenantCommissionPlans(this.tenantId, { page: 1, page_size: 100, status: true })
      .subscribe({
        next: (response:CommissionPlanResponse[]) => {
          this.availablePlans = response || [];
          this.loadingPlans = false;
          this.cdr.markForCheck();
        },
        error: (error) => {
          console.error('Error loading plans:', error);
          this.toastMessageService.showError(
            this.translateService.instant('COMMISSION_MANAGEMENT.STAFF_ASSIGNMENTS.ERROR_LOADING_PLANS')
          );
          this.loadingPlans = false;
          this.cdr.markForCheck();
        }
      });
  }

  assignPlan(): void {
    if (!this.selectedPlanId || !this.selectedStaffForAssignment) {
      return;
    }

    this.assigningPlan = true;
    this.cdr.markForCheck();

    this.commissionPlanService.assignPlanToStaff(this.selectedPlanId, {
      staff_id: this.selectedStaffForAssignment.staff_id
    }).subscribe({
      next: () => {
        this.toastMessageService.showSuccess(
          this.translateService.instant('COMMISSION_MANAGEMENT.STAFF_ASSIGNMENTS.PLAN_ASSIGNED_SUCCESS')
        );
        this.showAssignDialog = false;
        this.assigningPlan = false;
        this.loadStaffPlanAssignments();
        this.cdr.markForCheck();
      },
      error: (error:AppErrors) => {
        this.toastMessageService.showError(error);
        this.assigningPlan = false;
        this.cdr.markForCheck();
      }
    });
  }

  closeAssignDialog(): void {
    this.showAssignDialog = false;
    this.selectedStaffForAssignment = null;
    this.selectedPlanId = null;
    this.cdr.markForCheck();
  }

  getStaffStatusSeverity(status: StaffStatus): 'success' | 'info' | 'warn' | 'danger' | 'secondary' | 'contrast' {
    switch (status) {
      case StaffStatus.Active:
        return 'success';
      case StaffStatus.Invited:
        return 'info';
      case StaffStatus.Inactive:
        return 'danger';
      default:
        return 'secondary';
    }
  }

  getPlanStatusSeverity(status: PlanStatus): 'success' | 'info' | 'warn' | 'danger' | 'secondary' | 'contrast' {
    switch (status) {
      case PlanStatus.Active:
        return 'success';
      case PlanStatus.Inactive:
        return 'danger';
      default:
        return 'secondary';
    }
  }

  getAssignmentStatusSeverity(status: PlanStaffAssignmentStatus): 'success' | 'info' | 'warn' | 'danger' | 'secondary' | 'contrast' {
    switch (status) {
      case PlanStaffAssignmentStatus.Active:
        return 'success';
      case PlanStaffAssignmentStatus.InActive:
        return 'danger';
      default:
        return 'secondary';
    }
  }

  hasPlans(staff: StaffWithPlans): boolean {
    return staff.plans && staff.plans.length > 0;
  }

  getPlansCount(staff: StaffWithPlans): string {
    const count = staff.plans?.length || 0;
    return count === 1 
      ? `${count} ${this.translateService.instant('COMMISSION_MANAGEMENT.STAFF_ASSIGNMENTS.PLAN')}` 
      : `${count} ${this.translateService.instant('COMMISSION_MANAGEMENT.STAFF_ASSIGNMENTS.PLANS')}`;
  }
}
